/**
 * Tags Store - Pinia
 *
 * Manages user-defined tags for categorizing tracks, notes, and future POIs.
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import GeoMoverDB from '@/db/geoMoverDB';

export const useTagsStore = defineStore('tags', {
  state: () => ({
    tags: [],
    isLoaded: false
  }),

  getters: {
    /**
     * Get all tags
     */
    getAllTags: (state) => state.tags,

    /**
     * Get a tag by ID
     */
    getTagById: (state) => (id) => {
      return state.tags.find(t => t.id === id);
    },

    /**
     * Get multiple tags by IDs
     */
    getTagsByIds: (state) => (ids) => {
      if (!ids || !Array.isArray(ids)) return [];
      return state.tags.filter(t => ids.includes(t.id));
    },

    /**
     * Get tags sorted alphabetically by name
     */
    getTagsSorted: (state) => {
      return [...state.tags].sort((a, b) => a.name.localeCompare(b.name));
    }
  },

  actions: {
    /**
     * Load all tags from the database
     */
    async loadTags () {
      try {
        const tags = await GeoMoverDB.findAll('tags');
        this.tags = tags || [];
        this.isLoaded = true;
        appDebugLog.info('TagsStore', `Loaded ${this.tags.length} tags`);
      }
      catch (error) {
        appDebugLog.error('TagsStore', 'Error loading tags:', error);
        this.tags = [];
      }
    },

    /**
     * Create a new tag
     * @param {string} name - Tag name
     * @returns {Promise<number|null>} New tag ID or null on error
     */
    async createTag (name) {
      if (!name || !name.trim()) {
        appDebugLog.error('TagsStore', 'Cannot create tag with empty name');
        return null;
      }

      try {
        const newTag = {
          name: name.trim(),
          createdAt: Date.now()
        };

        const id = await GeoMoverDB.addOne('tags', newTag);
        newTag.id = id;
        this.tags.push(newTag);
        appDebugLog.info('TagsStore', 'Created tag:', name);
        return id;
      }
      catch (error) {
        appDebugLog.error('TagsStore', 'Error creating tag:', error);
        return null;
      }
    },

    /**
     * Update a tag's name
     * @param {number} id - Tag ID
     * @param {string} name - New name
     */
    async updateTag (id, name) {
      if (!name || !name.trim()) {
        appDebugLog.error('TagsStore', 'Cannot update tag with empty name');
        return false;
      }

      try {
        await GeoMoverDB.updateOne('tags', { name: name.trim() }, id);
        const tag = this.tags.find(t => t.id === id);
        if (tag) {
          tag.name = name.trim();
        }
        appDebugLog.info('TagsStore', 'Updated tag:', id, name);
        return true;
      }
      catch (error) {
        appDebugLog.error('TagsStore', 'Error updating tag:', error);
        return false;
      }
    },

    /**
     * Delete a tag
     * Note: Does NOT remove the tag from tracks/notes that reference it
     * @param {number} id - Tag ID
     */
    async deleteTag (id) {
      try {
        await GeoMoverDB.deleteOne('tags', id);
        this.tags = this.tags.filter(t => t.id !== id);
        appDebugLog.info('TagsStore', 'Deleted tag:', id);
        return true;
      }
      catch (error) {
        appDebugLog.error('TagsStore', 'Error deleting tag:', error);
        return false;
      }
    },

    /**
     * Find all tracks with a specific tag
     * Uses the multiEntry index for efficient querying
     * @param {number} tagId - Tag ID to search for
     * @returns {Promise<Array>} Tracks with the specified tag
     */
    async findTracksWithTag (tagId) {
      try {
        // Use the tagIds multiEntry index
        const tx = GeoMoverDB.db.transaction(['trackedRoutes'], 'readonly');
        const store = tx.objectStore('trackedRoutes');
        const index = store.index('tagIds');

        return new Promise((resolve, reject) => {
          const request = index.getAll(tagId);
          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => reject(request.error);
        });
      }
      catch (error) {
        appDebugLog.error('TagsStore', 'Error finding tracks with tag:', error);
        return [];
      }
    },

    /**
     * Count how many items use a specific tag
     * @param {number} tagId - Tag ID
     * @returns {Promise<number>} Usage count
     */
    async getTagUsageCount (tagId) {
      try {
        const tracks = await this.findTracksWithTag(tagId);
        // Could also check notes here if needed
        return tracks.length;
      }
      catch (error) {
        return 0;
      }
    }
  }
});
